class LabVideo extends HTMLElement {
  constructor() {
    super();

    this.shadow = this.attachShadow({mode: 'open'});

    const style = document.createElement('style');
    style.textContent = `
      :host {
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
      }
      :host > video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      :host > button[data-controls] {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 0;
        background: none;
        border: none;
        pointer-events: none;
        transition: opacity 0.2s;
        z-index: 1;
      }
      button[data-controls][hidden] {
        opacity: 0;
        pointer-events: none;
      }
    `;

    this.shadow.appendChild(style);

    this._boundTogglePlayPause = this.togglePlayPause.bind(this);
    this._boundOnPlay = this._onPlay.bind(this);
    this._boundOnPauseOrEnded = this._onPauseOrEnded.bind(this);
  }

  connectedCallback() {
    if (this.hasAttribute('src') || this.querySelectorAll('source').length > 0) {
      requestAnimationFrame(() => {
        this.initVideo();
        this.createSourceObserver();
        this.createVideoPlaybackObserver();
      });
    }
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.sourceObserver) {
      this.sourceObserver.disconnect();
      this.sourceObserver = null;
    }

    this.removeEventListeners();
  }

  initVideo() {
    this.video = document.createElement('video');

    const attributes = ['playsinline', 'autoplay', 'controls', 'muted', 'loop'];

    attributes.forEach((attr) => {
      if (this.hasAttribute(attr)) {
        this.video.setAttribute(attr, '');
        this.removeAttribute(attr);
      }
    });

    if (this.hasAttribute('poster')) {
      this.video.setAttribute('poster', this.getAttribute('poster'));
      this.removeAttribute('poster');
    }

    const sources = this.querySelectorAll('source');
    sources.forEach((source) => this.video.appendChild(source.cloneNode(true)));

    this.hasNativeControls = this.video.hasAttribute('controls');
    
    if (!this.hasNativeControls) {
      // Controls
      this.controlsButton = document.createElement('button');
      this.controlsButton.setAttribute('data-controls', '');
      this.controlsButton.hidden = this.video.hasAttribute('autoplay');
      this.controlsButton.innerHTML = `
        <svg width="93" height="93" viewBox="0 0 93 93" fill="none">
          <circle cx="46.2129" cy="46.791" r="45.9062" fill="white" fill-opacity="0.3"/>
          <circle cx="46.2129" cy="46.791" r="44.4062" stroke="white" stroke-width="3"/>
          <path d="M68.9717 46.791L34.8335 66.5007L34.8335 27.0813L68.9717 46.791Z" fill="white"/>
        </svg>
      `;
      this.shadow.appendChild(this.controlsButton);
    }

    if (this.video.hasAttribute('autoplay')) {
      // Fix bug on Chrome where muted property is set to false even with muted attribute
      this.video.muted = true;
    }

    this.addEventListeners();

    this.shadow.appendChild(this.video);
  }

  _onPlay() {
    this.setAttribute('data-playback', 'playing');

    if (!this.hasNativeControls && this.controlsButton) {
      this.controlsButton.setAttribute('hidden', '');
    }
  }

  _onPauseOrEnded() {
    this.setAttribute('data-playback', 'paused');

    if (!this.hasNativeControls && this.controlsButton) {
      this.controlsButton.hidden = this.video.hasAttribute('autoplay');
    }
  }

  addEventListeners(video = this.video) {
    if (!video) return;

    if (!video.hasAttribute('autoplay') && !this.hasNativeControls) {
      video.addEventListener('click', this._boundTogglePlayPause);
    }

    video.addEventListener('play', this._boundOnPlay);
    video.addEventListener('pause', this._boundOnPauseOrEnded);
    video.addEventListener('ended', this._boundOnPauseOrEnded);
  }

  removeEventListeners(video = this.video) {
    if (!video) return;

    if (!video.hasAttribute('autoplay') && !this.hasNativeControls) {
      video.removeEventListener('click', this._boundTogglePlayPause);
    }

    video.removeEventListener('play', this._boundOnPlay);
    video.removeEventListener('pause', this._boundOnPauseOrEnded);
    video.removeEventListener('ended', this._boundOnPauseOrEnded);
  }

  togglePlayPause() {
    if (!this.video) return;

    this.video.paused ? this.playVideo() : this.pauseVideo();
  }

  playVideo() {
    if (!this.video) return;

    const playPromise = this.video.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error('Video playback failed:', error);
      });
    }
  }

  pauseVideo() {
    if (!this.video) return;

    this.video.pause();
  }

  updateSources() {
    if (!this.video) return;

    const currentVideo = this.video;
    const preloadVideo = this.video.cloneNode(true);

    const newSources = Array.from(this.querySelectorAll('source')).map((source) =>
      source.cloneNode(true)
    );
    preloadVideo.querySelectorAll('source').forEach((source) => source.remove());
    newSources.forEach((source) => preloadVideo.appendChild(source));

    preloadVideo.muted = true; // Fix bug on Chrome where muted property is set to false even with muted attribute
    this.addEventListeners(preloadVideo);
    preloadVideo.load();

    preloadVideo.addEventListener(
      'canplaythrough',
      () => {
        const replaceVideo = () => {
          this.removeEventListeners(currentVideo);
          currentVideo.replaceWith(preloadVideo);
          this.video = preloadVideo;
        };

        if (!currentVideo.paused) {
          currentVideo.addEventListener('pause', replaceVideo, {once: true});
          currentVideo.pause();
        } else {
          replaceVideo();
        }
      },
      {once: true}
    );
  }

  createVideoPlaybackObserver() {
    if (this.intersectionObserver) return;

    const options = {
      threshold: 0.5,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (this.video.hasAttribute('autoplay')) {
          this.playVideo();
        }
      } else {
        this.pauseVideo();
      }
    }, options);

    this.intersectionObserver.observe(this);
  }

  createSourceObserver() {
    this.sourceObserver?.disconnect();

    this.sourceObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'childList' &&
          [...mutation.addedNodes, ...mutation.removedNodes].some(
            (node) => node.nodeName === 'SOURCE'
          )
        ) {
          this.updateSources();
          break;
        }
      }
    });

    this.sourceObserver.observe(this, {
      childList: true,
      subtree: false,
    });
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('lab-video')) {
  customElements.define('lab-video', LabVideo);
}